
import React from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Terrace, SunStatus } from "@/types/terrace";
import { Pencil, Trash2, User, MapPin } from "lucide-react";
import CoordinatesDisplay from "./CoordinatesDisplay";
import { useScreenSize } from "@/hooks/use-mobile";

interface TerraceTableProps {
  terraces: Terrace[];
  onEdit: (terrace: Terrace) => void;
  onDelete: (id: string) => void;
}

const TerraceTable = ({ terraces, onEdit, onDelete }: TerraceTableProps) => {
  const { isMobile } = useScreenSize();

  const getSunStatusBadge = (status: SunStatus) => {
    switch (status) {
      case 'sunny':
        return <Badge className="bg-peach-orange text-white text-xs">Sunny</Badge>;
      case 'soon':
        return <Badge className="bg-dusty-rose text-white text-xs">Soon</Badge>;
      case 'not-sunny':
        return <Badge className="bg-sunset-medium text-white text-xs">Not Sunny</Badge>;
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm("Are you sure you want to delete this terrace?")) {
      await onDelete(id);
    }
  };

  // Mobile card layout
  if (isMobile) {
    return (
      <div className="space-y-3">
        {terraces.map((terrace) => (
          <Card key={terrace.id} className="border-peach-orange/20 shadow-sm">
            <CardContent className="p-4">
              <div className="space-y-3">
                {/* Header with name and actions */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sunset-dark truncate">{terrace.name}</h3>
                    <p className="text-sm text-sunset-medium capitalize">{terrace.type}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {getSunStatusBadge(terrace.sunStatus)}
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="h-8 w-8 p-0 border-peach-orange/30"
                      onClick={() => onEdit(terrace)}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-8 w-8 p-0 border-peach-orange/30" 
                      onClick={() => handleDelete(terrace.id)}
                    >
                      <Trash2 className="h-3 w-3 text-coral-pink" />
                    </Button>
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-sunset-medium flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-sunset-medium">
                    <div className="leading-relaxed">{terrace.address}</div>
                    <CoordinatesDisplay lat={terrace.lat} lng={terrace.lng} />
                  </div>
                </div>

                {/* Submitter */}
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-sunset-medium flex-shrink-0" />
                  <span className="text-sm text-sunset-medium">
                    {terrace.submitted_by || 'Unknown'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  // Desktop table layout
  return (
    <Card className="border-peach-orange/20">
      <CardContent className="p-0 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead className="hidden md:table-cell">Type</TableHead>
              <TableHead className="hidden lg:table-cell">Address</TableHead>
              <TableHead className="hidden lg:table-cell">Coordinates</TableHead>
              <TableHead className="hidden xl:table-cell">Submitter</TableHead>
              <TableHead>Sun Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {terraces.map((terrace) => (
              <TableRow key={terrace.id}>
                <TableCell className="font-medium">
                  {terrace.name}
                  <div className="xl:hidden text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {terrace.submitted_by || 'Unknown'}
                  </div>
                  <div className="lg:hidden text-xs text-muted-foreground mt-1">
                    <CoordinatesDisplay lat={terrace.lat} lng={terrace.lng} />
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell capitalize">{terrace.type}</TableCell>
                <TableCell className="hidden lg:table-cell">{terrace.address}</TableCell>
                <TableCell className="hidden lg:table-cell">
                  <CoordinatesDisplay lat={terrace.lat} lng={terrace.lng} />
                </TableCell>
                <TableCell className="hidden xl:table-cell">
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <User className="h-3 w-3" />
                    {terrace.submitted_by || 'Unknown'}
                  </div>
                </TableCell>
                <TableCell>{getSunStatusBadge(terrace.sunStatus)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="h-8 w-8 p-0 border-peach-orange/30"
                      onClick={() => onEdit(terrace)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-8 w-8 p-0 border-peach-orange/30" 
                      onClick={() => handleDelete(terrace.id)}
                    >
                      <Trash2 className="h-4 w-4 text-coral-pink" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default TerraceTable;
